export const isKeyOf = <T extends object>(
  obj: T,
  key: string | number | symbol,
): key is keyof T => {
  return key in obj
}

export type FullName = {
  firstName: string
  lastName: string
  middlePart?: string
}

export const fullName = (name: FullName): string => {
  if (!name) {
    return ''
  }
  const middle = name.middlePart ? `${name.middlePart} ` : ''
  return `${name.firstName} ${middle}${name.lastName}`
}

export const partitionName = (name: string): FullName => {
  const nameParts = name.split(' ')
  const firstName = nameParts.shift()
  const lastName = nameParts.pop()

  return {
    firstName: firstName || '',
    lastName: lastName || '',
    middlePart: nameParts.join(' '),
  }
}

export const composeName = (firstName: string, lastName: string): FullName => {
  const firstNameParts = firstName.split(' ')
  const lastNameParts = lastName.split(' ')
  const firstNameResult = toProperCase(firstNameParts.shift())
  const lastNameResult = toProperCase(lastNameParts.pop())

  return {
    firstName: firstNameResult || '',
    lastName: lastNameResult || '',
    middlePart: `${firstNameParts.join(' ')} ${lastNameParts.join(' ')}`.trim(),
  }
}

export const decomposeName = (
  name: FullName,
): { firstName: string; lastName: string } => {
  const middle = name.middlePart ? `${name.middlePart} ` : ''

  return {
    firstName: name.firstName,
    lastName: middle + name.lastName,
  }
}

export const resizeImage = (src: string, w: number, dim: 'w' | 'h' = 'w') => {
  const urls = ['.based.dev', 'crowdbuilding.com', 'crowdbuilding.io']
  urls.forEach((url) => {
    if (src?.includes(url) && !src.includes(`${url}/cdn-cgi/image/`)) {
      src = src.replace(url, `${url}/cdn-cgi/image/${dim}=${Math.min(1920, w)}`)
    }
  })

  return src
}

const toProperCase = (str: string): string => {
  if (!str) {
    return str
  }

  return str[0].toUpperCase() + str.slice(1).toLowerCase()
}

export const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

export type AddressModel = {
  country?: { name: string }
  region?: { name: string }
  municipality?: { name: string }
  locality?: { name: string }
  city?: string
  street?: string
  postalCode?: string
}

/**
 * Extract a display string from a value that may be a plain string
 * or a Based localized text object (e.g. { nl: 'Amsterdam', en: 'Amsterdam' }).
 */
const toDisplayString = (value: unknown): string => {
  if (typeof value === 'string') {
    return value
  }
  if (value && typeof value === 'object') {
    // Localized text object — pick the first available locale value
    const vals = Object.values(value as Record<string, unknown>)
    for (const v of vals) {
      if (typeof v === 'string' && v.trim()) {
        return v
      }
    }
  }
  return ''
}

export const addressBuilder = (address: AddressModel): string | null => {
  const streetPart = address.street || ''
  const postalCodePart = address.postalCode || ''
  const locationPart =
    toDisplayString(address.locality?.name) ||
    toDisplayString(address.municipality?.name) ||
    address.city ||
    ''
  const regionPart = toDisplayString(address.region?.name)
  const countryPart = toDisplayString(address.country?.name)

  const postalLocationPart = [postalCodePart, locationPart]
    .filter(Boolean)
    .join(' ')

  const parts = [
    streetPart,
    postalLocationPart,
    regionPart,
    countryPart,
  ].filter(Boolean)

  return parts.length > 0 ? parts.join(', ') : null
}

export const labelAddressBuilder = (
  address: AddressModel,
  fallback: string,
): string => {
  return (
    address?.locality?.name ||
    address?.municipality?.name ||
    address?.region?.name ||
    address?.country?.name ||
    address?.street ||
    fallback
  )
}

export const compareSearchScore = (a: any, b: any): number => {
  const scoreA = a.$searchScore || 0
  const scoreB = b.$searchScore || 0
  return scoreB - scoreA
}

export const isUuid = (id: string): boolean => {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    id,
  )
}

export const createRandomGenerator = (seed: number) => {
  return function () {
    let t = (seed += 0x6d2b79f5)
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}
